import React, { useState } from "react";
import { Building2, Scale, Calculator, Users2, ChevronRight, ChevronLeft, CheckCircle2, Sparkles, Loader2, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { Card, Section, Btn } from "./ui";
import { ActionBadge } from "./ui";

const SECTORS = [
  { id: "legal", label: "Legal", icon: Scale, desc: "Law firms & legal practices" },
  { id: "accountancy", label: "Accountancy", icon: Calculator, desc: "Accounting, tax & advisory" },
  { id: "hr", label: "HR", icon: Users2, desc: "HR, recruitment & talent" },
];
const RISK = [
  { id: "conservative", label: "Conservative", desc: "Tighten defaults — warnings become masks." },
  { id: "balanced", label: "Balanced", desc: "Use the sector pack defaults as-is." },
];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    company_name: "", sector: "legal", jurisdiction: "", admin_name: "", admin_email: "",
    admin_password: "admin123", risk_profile: "balanced", departments: [],
  });
  const [deptInput, setDeptInput] = useState("");
  const [preview, setPreview] = useState(null);
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const steps = ["Company", "Sector", "Risk & departments", "Review policy", "Create"];

  const loadPreview = async () => {
    setBusy(true);
    const p = await api.onboardPreview(form);
    setPreview(p);
    setBusy(false);
    setStep(3);
  };

  const create = async () => {
    setBusy(true);
    const res = await api.onboard(form);
    setCreated(res.tenant);
    setBusy(false);
    setStep(4);
  };

  const canNext =
    (step === 0 && form.company_name && form.jurisdiction && form.admin_name && form.admin_email) ||
    (step === 1 && form.sector) ||
    step === 2;

  return (
    <Section title="Onboard a New Tenant" subtitle="Create a tenant, generate a starting policy, and review before activation.">
      {/* stepper */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex h-7 w-7 items-center justify-center rounded-full font-mono text-[11px] font-bold ${i <= step ? "bg-brand text-[#04121c]" : "border border-subtle text-slate-500"}`}>{i + 1}</div>
            <span className={`text-xs ${i <= step ? "text-white" : "text-slate-500"}`}>{s}</span>
            {i < steps.length - 1 && <ChevronRight className="h-3 w-3 text-slate-700" />}
          </div>
        ))}
      </div>

      <Card className="mx-auto max-w-2xl p-6">
        {step === 0 && (
          <div className="space-y-4">
            <Input label="Company name (fictional)" testid="ob-company" value={form.company_name} onChange={(v) => set("company_name", v)} placeholder="e.g. Harbourline Legal Group" />
            <Input label="Jurisdiction" testid="ob-jurisdiction" value={form.jurisdiction} onChange={(v) => set("jurisdiction", v)} placeholder="e.g. Brussels · Belgium (EU)" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Admin name" testid="ob-admin-name" value={form.admin_name} onChange={(v) => set("admin_name", v)} placeholder="Jane Admin" />
              <Input label="Admin email" testid="ob-admin-email" value={form.admin_email} onChange={(v) => set("admin_email", v)} placeholder="admin@firm.example" />
            </div>
            <Input label="Admin password" testid="ob-admin-pass" value={form.admin_password} onChange={(v) => set("admin_password", v)} />
          </div>
        )}

        {step === 1 && (
          <div className="grid grid-cols-3 gap-4">
            {SECTORS.map((s) => (
              <button key={s.id} data-testid={`ob-sector-${s.id}`} onClick={() => set("sector", s.id)} className="rounded-xl border p-5 text-left transition-colors" style={{ borderColor: form.sector === s.id ? "#0EA5E9" : "#1E2A3C", background: form.sector === s.id ? "rgba(14,165,233,0.08)" : "transparent" }}>
                <s.icon className="h-6 w-6 text-brand" />
                <div className="mt-3 font-display font-bold">{s.label}</div>
                <div className="text-xs text-slate-400">{s.desc}</div>
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div>
              <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">Risk appetite</div>
              <div className="grid grid-cols-2 gap-3">
                {RISK.map((r) => (
                  <button key={r.id} data-testid={`ob-risk-${r.id}`} onClick={() => set("risk_profile", r.id)} className="rounded-lg border p-3 text-left" style={{ borderColor: form.risk_profile === r.id ? "#0EA5E9" : "#1E2A3C", background: form.risk_profile === r.id ? "rgba(14,165,233,0.08)" : "transparent" }}>
                    <div className="text-sm font-semibold">{r.label}</div>
                    <div className="text-[11px] text-slate-400">{r.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">Departments (optional)</div>
              <div className="mb-2 flex gap-2">
                <input data-testid="ob-dept-input" value={deptInput} onChange={(e) => setDeptInput(e.target.value)} placeholder="Add a department" className="flex-1 rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm outline-none focus:border-brand" />
                <Btn variant="ghost" data-testid="ob-add-dept" onClick={() => { if (deptInput.trim()) { set("departments", [...form.departments, deptInput.trim()]); setDeptInput(""); } }}>Add</Btn>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.departments.map((d, i) => (
                  <span key={i} className="rounded-full border border-subtle bg-elevated px-3 py-1 text-xs text-slate-300">{d}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && preview && (
          <div>
            <div className="mb-3 flex items-center gap-2 text-sm text-slate-300"><Sparkles className="h-4 w-4 text-brand" /> Generated starting policy — {preview.sector_label}</div>
            <div className="overflow-hidden rounded-lg border border-subtle">
              {preview.rules.map((r) => (
                <div key={r.category} className="flex items-center justify-between border-b border-subtle/60 px-4 py-2.5">
                  <span className="text-sm text-slate-200">{r.label}</span>
                  <div className="flex items-center gap-2">
                    {r.sector_default !== r.proposed_action && <span className="font-mono text-[10px] text-slate-600 line-through">{r.sector_default}</span>}
                    <ActionBadge action={r.proposed_action} small />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-slate-500">You can fine-tune every rule later in the Policy Builder.</p>

            <div className="mt-4 flex items-start gap-3 rounded-lg border border-warn/30 bg-warn/10 p-4" data-testid="ob-verification-note">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-warn" />
              <div>
                <div className="text-sm font-semibold text-warn">Human review is always required</div>
                <p className="mt-1 text-[11px] leading-relaxed text-slate-300">
                  Every AI response returned through Gateway Chat starts as a <span className="font-semibold">Draft — Unverified</span>.
                  Your team can't copy or export it until a person ticks “I confirm I have reviewed this output”.
                  That confirmation (who reviewed it and when) is recorded in the Audit Log, so AI output is never used
                  externally without human oversight. This applies to every response, in every department.
                </p>
              </div>
            </div>
          </div>
        )}

        {step === 4 && created && (
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-allow/40 bg-allow/10"><CheckCircle2 className="h-7 w-7 text-allow" /></div>
            <h2 className="mt-4 font-display text-xl font-bold">{created.name} created</h2>
            {created.status === "active" ? (
              <p className="mt-3 text-sm text-allow">Tenant is now active and available on the login screen.</p>
            ) : (
              <>
                <p className="mt-1 text-sm text-slate-400">Status: <span className="font-mono text-warn">{created.status}</span>. Review complete — activate to allow logins.</p>
                <Btn data-testid="ob-activate" className="mt-5" onClick={async () => { const t = await api.activate(created.id); setCreated(t); }}>
                  Activate tenant
                </Btn>
              </>
            )}
          </div>
        )}

        {/* nav */}
        {step < 4 && (
          <div className="mt-6 flex items-center justify-between border-t border-subtle pt-4">
            <Btn variant="ghost" data-testid="ob-back" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}><ChevronLeft className="h-4 w-4" /> Back</Btn>
            {step < 2 && <Btn data-testid="ob-next" onClick={() => setStep(step + 1)} disabled={!canNext}>Next <ChevronRight className="h-4 w-4" /></Btn>}
            {step === 2 && <Btn data-testid="ob-preview" onClick={loadPreview} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate policy</Btn>}
            {step === 3 && <Btn data-testid="ob-create" onClick={create} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Create tenant</Btn>}
          </div>
        )}
      </Card>
    </Section>
  );
}

const Input = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <div className="mb-1.5 font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full rounded-lg border border-subtle bg-elevated px-3 py-2.5 text-sm outline-none focus:border-brand" />
  </div>
);
